import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetVendorDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  vendorId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the vendor detail page.
 *
 * Fetches business detail and vendor detail in a single HTTP round trip
 * on client-side navigation.
 */
export const getVendorDetailPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetVendorDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("vendorDetail.pageData", async () => {
      const [businessMod, vendorMod] = await Promise.all([
        import("../business/business.server"),
        import("./vendor.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getVendorForUser } = vendorMod;

      const [businessDetail, vendor] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        getVendorForUser(context.user.id, data.businessId, data.vendorId),
      ]);

      if (!vendor && data.vendorId !== "new") {
        // notFound() (not a bare Response) — thrown Responses can't be
        // serialized by the server RPC layer (Seroval crash → 500) and aren't
        // rejected properly on the client. notFound() renders the route's
        // notFoundComponent with a clean 404.
        throw notFound();
      }

      return { businessDetail, vendor } as const;
    });
  });
